/**
 * @description Main Company page with permission-gated tabs
 *
 * Key features:
 * - Tab navigation: Overview, Employees, Scheduling, Time Off
 * - Permission-based tab visibility
 * - Create dropdown with permission-gated options
 * - Employees only see Time Off tab
 * - `#merchant-alias` forces Overview so Add Alias can open Company Settings
 *
 * @dependencies
 * - PermissionGate, usePermissions: RBAC UI components
 * - PERMISSIONS: Permission code constants
 * - merchantAliasDeepLink: Add Alias hash forces Overview
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CompanyDashboard from './CompanyDashboard';
import EmployeeManagement from './employees/EmployeeManagement';
import CompanySchedulingTab from './scheduling/CompanySchedulingTab';
import CompanyTimeOffTab from './timeoff/CompanyTimeOffTab';
import TimeOffRequestModal from './timeoff/TimeOffRequestModal';
import PermissionGate from '../shared/PermissionGate';
import { usePermissions } from '../../contexts/PermissionContext';
import { PERMISSIONS } from '../../constants/permissionMappings';
import { isMerchantAliasDeepLink } from '../../utils/merchantAliasDeepLink';

const CompanyTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, isLoaded } = usePermissions();
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [timeOffRefreshKey, setTimeOffRefreshKey] = useState(0);
  const createDropdownRef = useRef(null);

  // All tabs with their required permissions
  // If permission is undefined, tab is always visible
  const allTabs = useMemo(() => [
    { key: 'overview', label: 'Overview', permission: PERMISSIONS.employees.view_all },
    { key: 'employees', label: 'Employees', permission: PERMISSIONS.employees.view_all },
    { key: 'scheduling', label: 'Scheduling', permission: PERMISSIONS.scheduling.create_shifts },
    { key: 'timeoff', label: 'Time Off', permission: PERMISSIONS.pto.view }
  ], []);

  // Filter visible tabs based on permissions
  const visibleTabs = useMemo(() => {
    if (!isLoaded) return [];
    return allTabs.filter(tab => !tab.permission || hasPermission(tab.permission));
  }, [allTabs, hasPermission, isLoaded]);

  // Check if user can create anything (show Create button)
  const canCreateAnything = useMemo(() => {
    if (!isLoaded) return false;
    return hasPermission(PERMISSIONS.employees.create) ||
      hasPermission(PERMISSIONS.scheduling.create_shifts) ||
      hasPermission(PERMISSIONS.time_clock.create_manual) ||
      hasPermission(PERMISSIONS.pto.request);
  }, [hasPermission, isLoaded]);

  // URL is the single source of truth for tab selection
  // Derive the current tab key from URL, with fallback to first visible tab
  const currentTabKey = useMemo(() => {
    // Add Alias must land on Overview so CompanyDashboard can open the settings modal
    if (isMerchantAliasDeepLink(location.hash)) {
      return 'overview';
    }

    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    // Check if the tab from URL is valid and visible
    if (tabParam && visibleTabs.some(t => t.key === tabParam)) {
      return tabParam;
    }

    // Fallback to first visible tab
    return visibleTabs[0]?.key || 'overview';
  }, [location.hash, location.search, visibleTabs]);

  // Handle tab click - update URL (single source of truth)
  const handleTabClick = (tabKey) => {
    const url = new URL(window.location);
    url.searchParams.set('tab', tabKey);
    // Preserve other params, replace to avoid history spam
    window.history.replaceState({}, '', url);
    // Force re-render by navigating (React Router will update location)
    navigate(url.pathname + url.search, { replace: true });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setIsCreateDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddEmployee = () => {
    setIsCreateDropdownOpen(false);
    const url = new URL(window.location);
    url.searchParams.set('tab', 'employees');
    url.searchParams.set('openModal', 'employee');
    navigate(location.pathname + url.search, { replace: true });
  };

  const handleAddShift = () => {
    setIsCreateDropdownOpen(false);
    const url = new URL(window.location);
    url.searchParams.set('tab', 'scheduling');
    url.searchParams.set('view', 'schedule');
    url.searchParams.set('openModal', 'shift');
    navigate(location.pathname + url.search, { replace: true });
  };

  const handleAddManualTime = () => {
    setIsCreateDropdownOpen(false);
    const url = new URL(window.location);
    url.searchParams.set('tab', 'scheduling');
    url.searchParams.set('view', 'timeEntries');
    url.searchParams.set('openModal', 'manualTime');
    navigate(location.pathname + url.search, { replace: true });
  };

  const handleAddTimeOffRequest = () => {
    setIsCreateDropdownOpen(false);
    // Navigate to time off tab via URL, then open modal
    handleTabClick('timeoff');
    setIsTimeOffModalOpen(true);
  };

  const handleTimeOffRequestSuccess = () => {
    // Navigate to time off tab and force list reload
    handleTabClick('timeoff');
    setTimeOffRefreshKey((k) => k + 1);
  };

  // Loading state while permissions load
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-[2000px] w-full mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Company</h1>
          {canCreateAnything && (
            <div className="relative" ref={createDropdownRef}>
              <button
                onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
                className="bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors focus:outline-none"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Create
              </button>
              {isCreateDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-100">
                  <PermissionGate permission={PERMISSIONS.employees.create}>
                    <button
                      onClick={handleAddEmployee}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      New Employee
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.scheduling.create_shifts}>
                    <button
                      onClick={handleAddShift}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-50"
                    >
                      New Shift
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.time_clock.create_manual}>
                    <button
                      onClick={handleAddManualTime}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-50"
                    >
                      Manual Time Entry
                    </button>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.pto.request}>
                    <button
                      onClick={handleAddTimeOffRequest}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-50"
                    >
                      Time Off Request
                    </button>
                  </PermissionGate>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs - Matching Transactions styling */}
        <div className="border-b mb-6">
          <nav className="flex space-x-8">
            {visibleTabs.map((tab) => {
              const isSelected = currentTabKey === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    isSelected
                      ? 'border-brand text-brand'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {currentTabKey === 'overview' && <CompanyDashboard />}
        {currentTabKey === 'employees' && <EmployeeManagement />}
        {currentTabKey === 'scheduling' && <CompanySchedulingTab />}
        {currentTabKey === 'timeoff' && <CompanyTimeOffTab refreshKey={timeOffRefreshKey} />}
      </div>

      {/* Time Off Request Modal */}
      <TimeOffRequestModal
        isOpen={isTimeOffModalOpen}
        onClose={() => setIsTimeOffModalOpen(false)}
        onSuccess={handleTimeOffRequestSuccess}
      />
    </div>
  );
};

export default CompanyTabs;
